export class CkeditorDragDropService {
  constructor(editor, component) {
    this.editor = editor;
    this.component = component;
    this.editable = editor.editable();
    this.CKEDITOR = component.$window.CKEDITOR;
    
    // Drag & drop state variables
    this.lastHighlighted = null;
    this.caretInHighlight = false;
    this.dropSide = null;
    this.newPlacement = null;
    this.defaultPlacement = null;
    this.tempDragElementClass = 'temp-drag-element-ais-10282025';
    this.tempDragElementWrapperClass = this.tempDragElementClass + '-wrapper';
    this.tempDragElementInnerWrapperClass = this.tempDragElementClass + '-inner-wrapper';
    this.afterPlacement = null;
    this.beforePlacement = null;
    this.dragSourceElement = null;
    this.ignoreDragElement = false;
    this.cellTags = ['TD', 'TH'];
    this.edgeThreshold = 5;
    
    this.init();
  }
  
  init() {
    this.setupEventListeners();
  }
  
  setupEventListeners() {
    this.editable.attachListener(this.editable, 'dragstart', (evt) => {
      this.dragSourceElement = evt.data.$.target;
      this.ignoreDragElement = this.dragSourceElement.classList.contains('cke_widget_drag_handler');
      console.log('start');
    });
    
    this.editable.attachListener(this.editable, 'dragover', (evt) => {
      if (this.ignoreDragElement) return;
      this.handleDragOver(evt);
    });
    
    this.editable.attachListener(this.editable, 'dragend', () => {
      this.clearHighlight();
      this.clearDragSource();
    });
    
    this.editable.attachListener(this.editable, 'drop', (evt) => {
      if (this.ignoreDragElement) return;
      this.handleDrop(evt);
    });
    
    this.editable.attachListener(this.editable, 'mouseleave', () => {
      this.clearHighlight();
    });
  }
  
  handleDragOver(evt) {
    const { clientX: x, clientY: y } = evt.data.$;
    const draggedEl = this.component.activeTag?.[0];
    if (!draggedEl) return;
    
    const elementUnderCursor = this.editor.document.$.elementFromPoint(x, y);
    this.newPlacement = null;
    if (!elementUnderCursor) {
      this.clearHighlight();
      return;
    }
    
    const targetContainer = elementUnderCursor.closest('[contenteditable="true"] *');
    if (!targetContainer) {
      this.clearHighlight();
      return;
    }
    
    // contains() fails due to ckeditor classes
    if (targetContainer === draggedEl || draggedEl.contains(targetContainer)) {
      this.clearHighlight();
      return;
    }
    
    if (draggedEl) {
      const srcRect = draggedEl.getBoundingClientRect();
      this.component.setDragSourceSelector(srcRect);
    }
    
    // Update highlight if target changed
    if (this.lastHighlighted !== targetContainer) {
      this.clearHighlight();
      this.lastHighlighted = targetContainer;
    }
    
    // Determine caret position and highlight state
    const { caretInHighlight: newCaretInHighlight, caretElement } = this.getCaretInfo(x, y, targetContainer);
    this.caretInHighlight = newCaretInHighlight;
    
    // Setup placement indicators
    const tempContainer = this.caretInHighlight ? caretElement : targetContainer;
    this.setupPlacementIndicators(draggedEl, tempContainer);
    
    // Calculate drop side based on mouse position
    const targetRect = targetContainer.getBoundingClientRect();
    const isCellTag = tempContainer && this.cellTags.includes(tempContainer.tagName);
    this.dropSide = isCellTag ? this.calculateDropSideForCell(x, y, targetRect): this.calculateDropSide(x, y, targetRect, this.caretInHighlight);
    
    // Apply visual feedback using newPlacement if available, otherwise use dropSide
    const placementIndicator = this.newPlacement || this.dropSide || 'full';
    const targetTagName = tempContainer ? tempContainer.tagName.toLowerCase() : '';
    this.component.setDragDropSelector(targetRect, placementIndicator, targetTagName);
  }
  
  handleDrop(evt) {
    this.clearHighlight();
    evt.data.preventDefault(true);
    evt.cancel();
    
    const { clientX: x, clientY: y } = evt.data.$;
    const draggedEl = this.component.activeTag?.[0];
    if (!draggedEl) return;
    
    const elementUnderCursor = this.editor.document.$.elementFromPoint(x, y);
    if (!elementUnderCursor) return;
    
    // Prevent dropping on widget wrappers, except report widgets
    const closestWidgetWrapper = elementUnderCursor.closest('.cke_widget_wrapper');
    if (closestWidgetWrapper && !closestWidgetWrapper.classList.contains('cke_widget_wrapper_fe-report')) {
      return;
    }
    
    // Check if dropping on the same element
    if (this.isDroppingOnSelf(x, y, draggedEl)) {
      console.log('Drop ignored, same element or inside itself!');
      return;
    }
    
    const container = elementUnderCursor.closest('[contenteditable="true"] *');
    if (!container) return;
    
    // Insert the element based on drop side
    this.insertDroppedElement(draggedEl, container, x, y);
  }
  
  clearDraggedTempElements() {
    const existingTempElements = this.editable.$.querySelectorAll(`.${this.tempDragElementClass}`);
    existingTempElements.forEach(el => el.remove());
    this.clearWrappers();
  }
  
  clearWrappers() {
    this.cleanInnerWrappers();
    const wrappers = this.editable.$.querySelectorAll(`.${this.tempDragElementWrapperClass}`);
    wrappers.forEach(wrapper => {
      if (wrapper.children.length === 0) {
        wrapper.remove();
      } else if (wrapper.children.length === 1) {
        const child = wrapper.firstElementChild;
        if (child.classList.contains(this.tempDragElementInnerWrapperClass)) {
          while (child.firstChild) {
            wrapper.insertBefore(child.firstChild, child);
          }
          child.remove();
          if (wrapper.children.length === 1) {
            const finalChild = wrapper.firstElementChild;
            wrapper.replaceWith(finalChild);
          } else if (wrapper.children.length === 0) {
            wrapper.remove();
          }
        } else {
          wrapper.replaceWith(child);
        }
      }
    });
    this.cleanInnerWrappers();
  }

  cleanInnerWrappers() {
    const innerWrappers = Array.from(this.editable.$.querySelectorAll(`div.${this.tempDragElementInnerWrapperClass}`));
    innerWrappers.forEach(innerWrapper => {
      if (!innerWrapper.parentElement) return;
      if (innerWrapper.childNodes.length === 0) {
        innerWrapper.remove();
        return;
      }
      if (innerWrapper.children.length === 1) {
        const child = innerWrapper.firstElementChild;
        if (!child || child.tagName !== 'DIV') return;
        // Case 1: Child is also an inner-wrapper div with same flex:1 style
        if (child.classList.contains(this.tempDragElementInnerWrapperClass)) {
          const outerStyle = innerWrapper.getAttribute('style') || '';
          const childStyle = child.getAttribute('style') || '';
          const outerFlex = outerStyle.match(/flex:\s*1/);
          const childFlex = childStyle.match(/flex:\s*1/);
          // Both have flex:1 - remove outer, keep inner
          if (outerFlex && childFlex) {
            innerWrapper.replaceWith(child);
            return;
          }
        }
        // Case 2: Child is a simple div (not a wrapper div) with only flex styles
        if (!child.classList.contains(this.tempDragElementWrapperClass) &&
            !child.classList.contains(this.tempDragElementInnerWrapperClass)) {
          const childStyle = child.getAttribute('style') || '';
          const hasFlex = childStyle.match(/flex:\s*1/);
          if (hasFlex) {
            const styleWithoutFlex = childStyle
              .replace(/flex:\s*1[^;]*/g, '')
              .replace(/flex-direction:\s*[^;]+/g, '')
              .replace(/;\s*;/g, ';')
              .replace(/^\s*;\s*|\s*;\s*$/g, '')
              .trim();
            if (styleWithoutFlex === '' || styleWithoutFlex === ';') {
              while (child.firstChild) innerWrapper.insertBefore(child.firstChild, child);
              child.remove();
            }
          }
        }
      }
    });
  }
  
  clearHighlight() {
    if (this.lastHighlighted) {
      this.component.setDragDropSelector(null, null, null);
      this.component.setDragSourceSelector(null);
      this.lastHighlighted = null;
    }
    this.clearDraggedTempElements();
  }
  
  clearDragSource() {
    this.dragSourceElement = null;
    this.ignoreDragElement = false;
  }

  triggerEditorChange() {
    try { this.editor.fire('saveSnapshot') } catch (e) { /* ignore */ }
    try { this.editor.fire('change') } catch (e) { /* ignore */ }
  }

  createInnerWrapper(content) {
    return `<div class="${this.tempDragElementInnerWrapperClass}" style="flex:1;">${content}</div>`;
  }
  
  buildOutterWrapperHTML(childrenHtmlArray, verticalDrop) {
    const flexDirection = verticalDrop ? 'column' : 'row';
    const children = childrenHtmlArray.join('');
    const style = `display: flex; flex-direction: ${flexDirection}; flex: 1;`;
    return `<div style="${style}" class="${this.tempDragElementWrapperClass}">${children}</div>`;
  }
  
  getCaretInfo(x, y, targetContainer) {
    const domRange = this.editor.document.$.caretRangeFromPoint 
      ? this.editor.document.$.caretRangeFromPoint(x, y) 
      : this.editor.document.$.caretRangeFromPoint(x, y);
    
    if (!domRange) return { caretInHighlight: false, caretElement: null };
    
    const caretNode = domRange.startContainer;
    const caretElement = caretNode.nodeType === Node.TEXT_NODE 
      ? caretNode.parentElement 
      : caretNode;
    
    return {
      caretInHighlight: targetContainer === caretElement,
      caretElement
    };
  }
  
  findCommonParent(element1, element2) {
    if (!element1 || !element2) return null;
    let parent = element1.parentElement;
    while (parent) {
      if (parent.contains(element2)) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return null;
  }

  setupPlacementIndicators(draggedEl, tempContainer) {
    this.afterPlacement = 'right'; 
    this.beforePlacement = 'left';
    
    // Use calculateDropSideForCell instead
    if (this.cellTags.includes(tempContainer.tagName)) return;
    
    const commonParent = this.findCommonParent(draggedEl, tempContainer);
    if (!commonParent) return;

    const parentClone = commonParent.cloneNode(true);
    const computedStyle = this.component.$window.getComputedStyle(commonParent);
    parentClone.style.width = computedStyle.width;
    parentClone.style.height = computedStyle.height;
    parentClone.style.display = computedStyle.display;
    parentClone.style.flexDirection = computedStyle.flexDirection;
    
    const allChildren = Array.from(commonParent.children);
    const draggedIndex = allChildren.indexOf(draggedEl);
    const targetIndex = allChildren.indexOf(tempContainer);
    if (draggedIndex === -1 || targetIndex === -1) return;
    
    const draggedClone = parentClone.children[draggedIndex];
    const targetClone = parentClone.children[targetIndex];
    if (!draggedClone || !targetClone) return;
    
    draggedClone.remove();
    
    const adjustedTargetIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;
    const adjustedTargetClone = parentClone.children[adjustedTargetIndex];
    if (!adjustedTargetClone) return;
    
    const beforeClone = draggedClone.cloneNode(true);
    adjustedTargetClone.insertAdjacentElement('beforebegin', beforeClone);
    const afterClone = draggedClone.cloneNode(true);
    adjustedTargetClone.insertAdjacentElement('afterend', afterClone);
    parentClone.style.position = 'absolute';
    parentClone.style.visibility = 'hidden';
    parentClone.style.pointerEvents = 'none';
    parentClone.style.top = '-9999px';
    parentClone.style.left = '-9999px';
    document.body.appendChild(parentClone);
    
    const targetRect = adjustedTargetClone.getBoundingClientRect();
    const beforeRect = beforeClone.getBoundingClientRect();
    const afterRect = afterClone.getBoundingClientRect();
    if (beforeRect.top !== targetRect.top) this.beforePlacement = 'top'; 
    if (afterRect.top !== targetRect.top) this.afterPlacement = 'bottom';
    document.body.removeChild(parentClone);
  }
  
  calculateDropSideForCell(x, y, targetRect) {
    const relativeX = x - targetRect.left;
    const relativeY = y - targetRect.top;
    const distToTop = relativeY;
    const distToBottom = targetRect.height - relativeY;
    const distToLeft = relativeX;
    const distToRight = targetRect.width - relativeX;
    const minDist = Math.min(distToTop, distToBottom, distToLeft, distToRight);
    
    if (minDist <= this.edgeThreshold) {
      if (minDist === distToTop) return 'top';
      if (minDist === distToBottom) return 'bottom';
      if (minDist === distToLeft) return 'left';
      if (minDist === distToRight) return 'right';
    }
    return null;
  }
  
  calculateClosestEdge(x, y, targetRect) {
    const relativeX = x - targetRect.left;
    const relativeY = y - targetRect.top;
    const distanceToTop = relativeY;
    const distanceToBottom = targetRect.height - relativeY;
    const distanceToLeft = relativeX;
    const distanceToRight = targetRect.width - relativeX;
    const minDistance = Math.min(distanceToTop, distanceToBottom, distanceToLeft, distanceToRight);
    
    if (minDistance === distanceToTop && distanceToTop <= this.edgeThreshold) {
      this.newPlacement = 'top';
      this.defaultPlacement = this.beforePlacement;
      return this.beforePlacement;
    } else if (minDistance === distanceToBottom && distanceToBottom <= this.edgeThreshold) {
      this.newPlacement = 'bottom';
      this.defaultPlacement = this.afterPlacement;
      return this.afterPlacement;
    } else if (minDistance === distanceToLeft && distanceToLeft <= this.edgeThreshold) {
      this.newPlacement = 'left';
      this.defaultPlacement = this.beforePlacement;
      return this.beforePlacement;
    } else if (minDistance === distanceToRight && distanceToRight <= this.edgeThreshold) {
      this.newPlacement = 'right';
      this.defaultPlacement = this.afterPlacement;
      return this.afterPlacement;
    }
    
    return null; // Not close to any edge
  }
  
  calculateDropSide(x, y, targetRect, caretInHighlight) {
    const closestEdgeResult = this.calculateClosestEdge(x, y, targetRect);
    if (closestEdgeResult !== null) {
      return closestEdgeResult;
    }
    
    // Fallback logic when not close to any edge
    const relativeX = x - targetRect.left;
    const relativeY = y - targetRect.top;
    
    if (caretInHighlight) {
      // Simple edge threshold fallback for caret in highlight
      if (relativeX <= this.edgeThreshold) return this.beforePlacement;
      if (relativeX >= targetRect.width - this.edgeThreshold) return this.afterPlacement;
      if (relativeY <= this.edgeThreshold) return this.beforePlacement;
      if (relativeY >= targetRect.height - this.edgeThreshold) return this.afterPlacement;
      return null; // Will use 'full' as fallback
    } else {
      // Half-split fallback for normal drag
      if (this.beforePlacement === 'left') {
        const containerCenterX = targetRect.left + (targetRect.width / 2);
        return x < containerCenterX ? this.beforePlacement : this.afterPlacement;
      } else if (this.beforePlacement === 'top') {
        const containerCenterY = targetRect.top + (targetRect.height / 2);
        return y < containerCenterY ? this.beforePlacement : this.afterPlacement;
      }
    }
  }
  
  isDroppingOnSelf(x, y, draggedEl) {
    const rect = draggedEl.getBoundingClientRect();
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
  }
  getActualDropDirection() {
    if (this.newPlacement) return this.newPlacement;
    return this.dropSide;
  }
  
  insertAtCaretPosition(draggedEl, x, y) {
    const sel = this.editor.getSelection();
    const domRange = this.editor.document.$.caretRangeFromPoint
      ? this.editor.document.$.caretRangeFromPoint(x, y)
      : this.editor.document.$.caretRangeFromPoint(x, y);
    if (domRange) {
      const ckRange = new this.CKEDITOR.dom.range(this.editor.document);
      ckRange.setStart(new this.CKEDITOR.dom.node(domRange.startContainer), domRange.startOffset);
      ckRange.collapse(true);
      sel.selectRanges([ckRange]);
    }
    this.editor.insertHtml(draggedEl.outerHTML);
    this.component.removeBlock(this.component.activeTag, false);
  }
  
  insertAtDropPosition(draggedEl, container, _x, _y) {
    const actualDirection = this.getActualDropDirection();
    const verticalDrop = actualDirection === 'top' || actualDirection === 'bottom';
    const dropBefore = (actualDirection === 'top' || actualDirection === 'left');
    const insertMethod = dropBefore ? 'beforebegin' : 'afterend';
    const placementOverride = this.newPlacement && this.newPlacement !== this.defaultPlacement;
    let newHTML = draggedEl.outerHTML;
    if (placementOverride) {
      let containerHTML = container.outerHTML;
      let draggedHTML = draggedEl.outerHTML;
      // Dragged element is already a wrapper
      if (draggedEl.classList.contains(this.tempDragElementWrapperClass)) {
        const wrappedHTML = this.createInnerWrapper(containerHTML);
        const innerInsertMethod = dropBefore ? 'beforeend' : 'afterbegin';
        draggedEl.insertAdjacentHTML(innerInsertMethod, wrappedHTML);
        container.replaceWith(draggedEl.cloneNode(true));
        this.component.removeBlock(this.component.activeTag, false);
      } else {
        const [firstHTML, secondHTML] = dropBefore ? [draggedHTML, containerHTML] : [containerHTML, draggedHTML];
        const child1 = this.createInnerWrapper(firstHTML);
        const child2 = this.createInnerWrapper(secondHTML);
        newHTML = this.buildOutterWrapperHTML([child1, child2], verticalDrop);
        container.insertAdjacentHTML(insertMethod, newHTML);
        container.remove();
        this.component.removeBlock(this.component.activeTag, false);
      }
    }
    else {
      container.insertAdjacentHTML(insertMethod, newHTML);
      this.component.removeBlock(this.component.activeTag, false);
    }
  }

  isCellEmpty(cell) {
    if (!cell) return true;
    return cell.childNodes.length === 0;
  }
  
  getWrapperDirection(wrapper) {
    if (!wrapper || !wrapper.classList.contains(this.tempDragElementWrapperClass)) return null;
    const style = wrapper.getAttribute('style') || '';
    if (style.includes('flex-direction: column')) return 'vertical';
    if (style.includes('flex-direction: row')) return 'horizontal';
    return null;
  }
  
  insertAtCellTag(draggedEl, cell, direction) {
    const verticalDrop = direction === 'top' || direction === 'bottom';
    const dropBefore = (direction === 'top' || direction === 'left');
    const requiredDirection = verticalDrop ? 'vertical' : 'horizontal';
    if (this.isCellEmpty(cell)) {
      cell.insertAdjacentHTML('beforeend', draggedEl.outerHTML);
      this.component.removeBlock(this.component.activeTag, false);
      return;
    }
    const firstChild = cell.firstElementChild;
    const isWrapped = firstChild && firstChild.classList.contains(this.tempDragElementWrapperClass);
    const wrapperDirection = isWrapped ? this.getWrapperDirection(firstChild) : null;
    if (isWrapped && wrapperDirection === requiredDirection) {
      const wrappedHTML = this.createInnerWrapper(draggedEl.outerHTML);
      const innerInsertMethod = dropBefore ? 'afterbegin' : 'beforeend';
      firstChild.insertAdjacentHTML(innerInsertMethod, wrappedHTML);
      this.component.removeBlock(this.component.activeTag, false);
    } else {
      const cellContentHTML = cell.innerHTML || '';
      const draggedHTML = draggedEl.outerHTML;
      const [firstHTML, secondHTML] = dropBefore ? [draggedHTML, cellContentHTML] : [cellContentHTML, draggedHTML];
      const child1 = this.createInnerWrapper(firstHTML);
      const child2 = this.createInnerWrapper(secondHTML);
      const newHTML = this.buildOutterWrapperHTML([child1, child2], verticalDrop);
      cell.innerHTML = newHTML;
      this.component.removeBlock(this.component.activeTag, false);
    }
  }
  
  insertDroppedElement(draggedEl, container, x, y) {
    if (container && this.cellTags.includes(container.tagName)) {
      const actualDirection = this.getActualDropDirection();
      const isSideDrop = actualDirection === 'top' || actualDirection === 'left' || 
                         actualDirection === 'bottom' || actualDirection === 'right';
      if (isSideDrop) {
        this.insertAtCellTag(draggedEl, container, actualDirection);
      } else {
        this.insertAtCaretPosition(draggedEl, x, y);
      }
    } else {
      if (this.caretInHighlight && !this.dropSide) this.insertAtCaretPosition(draggedEl, x, y);
      else this.insertAtDropPosition(draggedEl, container, x, y);
    }
    this.clearWrappers();
    this.triggerEditorChange();
  }
}
